export const NON_GEOCODING_PIN_SIZE = 15;
export const MISSING_VALUE = "—";
export const NOT_AVAILABLE_VALUE = "N/A";

export const AIR_ROOF_GEOMETRY_CODE_TO_DESC_MAPPING: Record<string, string> = {
  "0": "Unknown/default",
  "1": "Flat",
  "2": "Gable end without bracing",
  "3": "Hip",
  "4": "Complex",
  "5": "Stepped",
  "6": "Shed",
  "7": "Mansard",
  "8": "Gable end with bracing",
  "9": "Pyramid",
  "10": "Gambrel",
};

export const AIR_ROOF_COVERING_CODE_TO_DESC_MAPPING: Record<string, string> = {
  "0": "Unknown (Default)",
  "1": "Asphalt Shingles",
  "2": "Wooden Shingles",
  "3": "Clay / Concrete Tiles",
  "4": "Light Metal Panels",
  "5": "Slate",
  "6": "Built-up Roof with Gravel",
  "7": "Single Ply Membrane",
  "8": "Standing Seam Metal Roof",
  "9": "Built-up Roof without Gravel",
  "10": "Single Ply Membrane - Ballasted",
  "11": "Hurricane Wind-Rated Roof Coverings",
};

export const PING_CONSTRUCTION_CODE_TO_DESC_MAPPING = {
  WF: "Wood Frame",
  MAS: "Masonry",
  SF: "Steel Frame",
  LM: "Light Metal",
  RC: "Reinforced Concrete",
  TU: "Tilt-up",
  MOB: "Mobile Homes",
  NON: "Non-Building",
  UNK: "Unknown",
  ERR: "Missing",
};

export const SOV_SUMMARY_LABEL_LENGTH_THRESHOLDS = {
  SHORT: 15,
  LONG: 15,
  EXTRA_LONG: 19,
  LOCATION: 30,
};

export const PING_YEAR_BUILT_BANDS_MAP = {
  "Pre-1950": { const__bldg_year_built_lte: 1950 },
  "1950 - 1959": {
    const__bldg_year_built_gte: 1950,
    const__bldg_year_built_lte: 1959,
  },
  "1960 - 1969": {
    const__bldg_year_built_gte: 1960,
    const__bldg_year_built_lte: 1969,
  },
  "1970 - 1979": {
    const__bldg_year_built_gte: 1970,
    const__bldg_year_built_lte: 1979,
  },
  "1980 - 1989": {
    const__bldg_year_built_gte: 1980,
    const__bldg_year_built_lte: 1989,
  },
  "1990 - 1999": {
    const__bldg_year_built_gte: 1990,
    const__bldg_year_built_lte: 1999,
  },

  "2000 - 2009": {
    const__bldg_year_built_gte: 2000,
    const__bldg_year_built_lte: 2009,
  },
  "2010 - 2019": {
    const__bldg_year_built_gte: 2010,
    const__bldg_year_built_lte: 2019,
  },
  "2020+": { const__bldg_year_built_gte: 2020 },

  NULL: {
    const__bldg_year_built_gte: -1,
    const__bldg_year_built_lte: -1,
  },
};

export const BREAKDOWN_FIELDS = [
  "country",
  "state",
  "county",
  "wind_tier",
  "fema_flood_zone",
  "const__bldg_year_built",
  "slosh_zone",
  "total_crime_grade",
  "hail_riskr",
  "wfir_riskr",
  "distance_to_coast_miles",
  "ping_geo__match_level",
];

export const PING_FILTER_ALL = "all";
export const PING_FILTER_NULL = "NULL";

export const PING_YEAR_BUILT_BANDS = [
  { label: "Pre-1950", value: "Pre-1950" },
  { label: "1950 - 1959", value: "1950 - 1959" },
  { label: "1960 - 1969", value: "1960 - 1969" },
  { label: "1970 - 1979", value: "1970 - 1979" },
  { label: "1980 - 1989", value: "1980 - 1989" },
  { label: "1990 - 1999", value: "1990 - 1999" },
  { label: "2000 - 2009", value: "2000 - 2009" },
  { label: "2010 - 2019", value: "2010 - 2019" },
  { label: "2020+", value: "2020+" },
];

export const PING_LAYER_ANALYSIS_OPTIONS = [
  { label: "No layer", value: "0x0" },
  { label: "Primary 10", value: "0x10" },
  { label: "15x10", value: "15x10" },
  { label: "25x25", value: "25x25" },
  { label: "50x50", value: "50x50" },
  { label: "100x100", value: "100x100" },
  { label: "200x200", value: "200x200" },
];

export const PING_LAYER_MAP_OPTIONS = {
  "0x0": { attach: null, layer_limit: null, exhaust: null },
  "0x10": { attach: 0, layer_limit: 10000000, exhaust: 10000000 },
  "15x10": { attach: 10000000, layer_limit: 15000000, exhaust: 25000000 },
  "25x25": { attach: 25000000, layer_limit: 25000000, exhaust: 50000000 },
  "50x50": { attach: 50000000, layer_limit: 50000000, exhaust: 100000000 },
  "100x100": { attach: 100000000, layer_limit: 100000000, exhaust: 200000000 },
  "200x200": { attach: 200000000, layer_limit: 200000000, exhaust: 400000000 },
};

export type PingLayerMapKey = keyof typeof PING_LAYER_MAP_OPTIONS;

export const findLayerMapKey = (attach: number, layerLimit: number) => {
  return Object.entries(PING_LAYER_MAP_OPTIONS).find(
    ([, value]) => value.attach === attach && value.layer_limit === layerLimit,
  )?.[0] as PingLayerMapKey;
};

export const GLOBAL_MAP_MIN_ZOOM = 1;
export const GLOBAL_MAP_MAX_ZOOM = 25;

export const DEFAULT_MAP_ZOOM = 18;
export const DEFAULT_MAX_ZOOM = 22;
export const DEFAULT_MIN_ZOOM = 10;

export const HEX_BLG_THRESHOLD = 500; // if buildings.length > HEX_BLG_THRESHOLD, then show hexagons
export const DEFAULT_PLI_ZOOM = 15;
